import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API_URL, getAuthHeaders, formatDateTime, getManualCheckReasons } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { useApiMessages } from "../hooks/useApiMessages";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "../components/ui/pagination";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Badge } from "../components/ui/badge";
import { CheckpointMap } from "../components/Map/CheckpointMap";
import { MapContainer, TileLayer, Marker, Popup, Circle } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { feetToMeters } from "../lib/utils";
import { ClipboardList, Search, Filter, MapPin, User, AlertTriangle, Map, Trash2, CheckCircle2, Image, Calendar, X, ListChecks, CheckCircle, FileText, Video, Download } from "lucide-react";
import { toast } from "sonner";

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const logIcon = L.divIcon({
  className: "log-marker",
  html: `<div style="
    background-color: #3B82F6;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 3px solid white;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
  "></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

export const PatrolLogs = () => {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiMessages();
  const { isAdmin, isRevendedor } = useAuth();
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [checkpoints, setCheckpoints] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [filters, setFilters] = useState({
    user_id: "",
    route_id: "",
    search: "",
  });
  const [selectedLog, setSelectedLog] = useState(null);
  const [isMapDialogOpen, setIsMapDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isPhotoDialogOpen, setIsPhotoDialogOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [logToDelete, setLogToDelete] = useState(null);
  const [isInstructionsDialogOpen, setIsInstructionsDialogOpen] = useState(false);
  const [selectedLogForInstructions, setSelectedLogForInstructions] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Calcular el último mes por defecto (primer día del mes anterior hasta hoy)
  const getDefaultDateRange = () => {
    const today = new Date();
    const firstDayOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfPreviousMonth = new Date(firstDayOfCurrentMonth);
    lastDayOfPreviousMonth.setDate(0); // Último día del mes anterior
    const firstDayOfPreviousMonth = new Date(lastDayOfPreviousMonth.getFullYear(), lastDayOfPreviousMonth.getMonth(), 1);
    
    // Formatear como YYYY-MM-DD
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      start: formatDate(firstDayOfPreviousMonth),
      end: formatDate(today)
    };
  };
  
  const defaultDateRange = getDefaultDateRange();
  const [startDate, setStartDate] = useState(defaultDateRange.start);
  const [endDate, setEndDate] = useState(defaultDateRange.end);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        
        if (selectedCompany && selectedCompany !== "all") {
          params.append("company_id", selectedCompany);
        }
        if (startDate) {
          params.append("start_date", startDate);
        }
        if (endDate) {
          params.append("end_date", endDate);
        }
        
        const logsUrl = params.toString() 
          ? `${API_URL}/patrol/logs?${params.toString()}`
          : `${API_URL}/patrol/logs`;
        
        const [logsRes, usersRes, routesRes, checkpointsRes, companiesRes] = await Promise.all([
          axios.get(logsUrl, { headers: getAuthHeaders() }),
          axios.get(`${API_URL}/users`, { headers: getAuthHeaders() }),
          axios.get(`${API_URL}/routes`, { headers: getAuthHeaders() }),
          axios.get(`${API_URL}/checkpoints`, { headers: getAuthHeaders() }),
          axios.get(`${API_URL}/companies`, { headers: getAuthHeaders() }),
        ]);
        setLogs(Array.isArray(logsRes.data) ? logsRes.data : []);
        setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
        setRoutes(Array.isArray(routesRes.data) ? routesRes.data : []);
        setCheckpoints(Array.isArray(checkpointsRes.data) ? checkpointsRes.data : []);
        setCompanies(Array.isArray(companiesRes.data) ? companiesRes.data : []);
      } catch (error) {
        console.error("Error fetching logs:", error);
        setLogs([]);
        setUsers([]);
        setRoutes([]);
        setCheckpoints([]);
        setCompanies([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedCompany, startDate, endDate]);

  const getUserName = (userId) => {
    if (!Array.isArray(users)) return "Desconocido";
    const user = users.find((u) => u.id === userId);
    return user?.name || "Desconocido";
  };

  const getCheckpointName = (checkpointId) => {
    if (!Array.isArray(checkpoints)) return "Desconocido";
    const cp = checkpoints.find((c) => c.id === checkpointId);
    return cp?.name || "Desconocido";
  };

  const getCheckpoint = (checkpointId) => {
    if (!Array.isArray(checkpoints)) return null;
    return checkpoints.find((c) => c.id === checkpointId);
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000; // Radio de la Tierra en metros
    const phi1 = (lat1 * Math.PI) / 180;
    const phi2 = (lat2 * Math.PI) / 180;
    const deltaPhi = ((lat2 - lat1) * Math.PI) / 180;
    const deltaLambda = ((lon2 - lon1) * Math.PI) / 180;
    
    const a =
      Math.sin(deltaPhi / 2) ** 2 +
      Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    
    return R * c; // Distancia en metros
  };

  const hasLocationDiscrepancy = (log) => {
    const checkpoint = getCheckpoint(log.checkpoint_id);
    if (!checkpoint) return false;
    
    const distance = calculateDistance(
      log.latitude,
      log.longitude,
      checkpoint.latitude,
      checkpoint.longitude
    );
    
    return distance > 100; // Más de 100 metros
  };

  const getDistanceFromCheckpoint = (log) => {
    const checkpoint = getCheckpoint(log.checkpoint_id);
    if (!checkpoint) return null;
    
    return calculateDistance(
      log.latitude,
      log.longitude,
      checkpoint.latitude,
      checkpoint.longitude
    );
  };

  const handleMarkAsReviewed = async (log) => {
    try {
      const response = await axios.put(
        `${API_URL}/patrol/logs/${log.id}/review`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(t("pages.patrolLogs.markAsReviewed"));
      // Actualizar el log en el estado
      setLogs(prevLogs => 
        prevLogs.map(l => l.id === log.id ? { ...l, is_reviewed: true } : l)
      );
    } catch (error) {
      console.error("Error al marcar como revisado:", error);
      toast.error(getErrorMessage(error, "api.errors.markReviewedError"));
    }
  };

  const getRouteName = (routeId) => {
    if (!Array.isArray(routes)) return t("pages.dashboard.unknown");
    const route = routes.find((r) => r.id === routeId);
    return route?.name || t("pages.dashboard.unknown");
  };

  const getManualReasonLabel = (reason) => {
    const found = getManualCheckReasons(t).find((r) => r.value === reason);
    return found?.label || reason;
  };

  const filteredLogs = Array.isArray(logs) ? logs.filter((log) => {
    if (filters.user_id && log.user_id !== filters.user_id) return false;
    if (filters.route_id && log.route_id !== filters.route_id) return false;
    return true;
  }) : [];

  // Paginación
  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedLogs = filteredLogs.slice(startIndex, endIndex);

  // Resetear a página 1 cuando cambian los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [filters.user_id, filters.route_id, selectedCompany, startDate, endDate]);

  const openMapDialog = (log) => {
    setSelectedLog(log);
    setIsMapDialogOpen(true);
  };

  const handleDeleteClick = (log) => {
    setLogToDelete(log);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!logToDelete) return;
    
    try {
      await axios.delete(
        `${API_URL}/patrol/logs/${logToDelete.id}`,
        { headers: getAuthHeaders() }
      );
      toast.success(t("pages.patrolLogs.recordDeleted"));
      setLogs(Array.isArray(logs) ? logs.filter((log) => log.id !== logToDelete.id) : []);
      setIsDeleteDialogOpen(false);
      setLogToDelete(null);
    } catch (error) {
      console.error("Error al eliminar registro:", error);
      toast.error(getErrorMessage(error, "api.errors.deleteError"));
    }
  };

  return (
    <div className="space-y-6" data-testid="patrol-logs-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">
          {t("pages.patrolLogs.title")}
        </h1>
        <p className="text-slate-600 mt-1">
          {t("pages.patrolLogs.subtitle")}
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Filtro por compañía - Solo para admin y revendedor */}
            {(isAdmin() || isRevendedor()) && (
              <Select
                value={selectedCompany}
                onValueChange={(value) => {
                  setSelectedCompany(value);
                  setLoading(true);
                }}
              >
                <SelectTrigger data-testid="filter-company">
                  <SelectValue placeholder={t("pages.routes.filterByCompany")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("pages.routes.allCompanies")}</SelectItem>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Select
              value={filters.user_id}
              onValueChange={(value) =>
                setFilters({ ...filters, user_id: value === "all" ? "" : value })
              }
            >
              <SelectTrigger data-testid="filter-user">
                <SelectValue placeholder={t("pages.patrolLogs.filterByGuard")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("pages.patrolLogs.allGuards")}</SelectItem>
                {users
                  .filter((u) => u.role === "vigilante")
                  .map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.route_id}
              onValueChange={(value) =>
                setFilters({ ...filters, route_id: value === "all" ? "" : value })
              }
            >
              <SelectTrigger data-testid="filter-route">
                <SelectValue placeholder={t("pages.patrolLogs.filterByRoute")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("pages.patrolLogs.allRoutes")}</SelectItem>
                {routes.map((route) => (
                  <SelectItem key={route.id} value={route.id}>
                    {route.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Filter className="h-4 w-4" />
                  {t("pages.patrolLogs.recordsFound", { count: filteredLogs.length })}
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-slate-600">{t("pages.users.perPage")}</label>
                  <Select
                    value={itemsPerPage.toString()}
                    onValueChange={(value) => {
                      setItemsPerPage(parseInt(value));
                      setCurrentPage(1);
                    }}
                  >
                    <SelectTrigger className="w-[80px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            
            {/* Filtro por rango de fechas */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-slate-400" />
                <label className="text-sm font-medium text-slate-700 whitespace-nowrap">{t("pages.patrolLogs.startDate")}</label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setLoading(true);
                  }}
                  className="flex-1"
                  data-testid="filter-start-date"
                />
                {startDate && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => {
                      setStartDate("");
                      setLoading(true);
                    }}
                    title={t("pages.patrolLogs.clearStartDate")}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-slate-400" />
                <label className="text-sm font-medium text-slate-700 whitespace-nowrap">{t("pages.patrolLogs.endDate")}</label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setLoading(true);
                  }}
                  className="flex-1"
                  min={startDate || undefined}
                  data-testid="filter-end-date"
                />
                {endDate && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => {
                      setEndDate("");
                      setLoading(true);
                    }}
                    title={t("pages.patrolLogs.clearEndDate")}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              
              {(startDate || endDate) && (
                <div className="flex items-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setStartDate("");
                      setEndDate("");
                      setLoading(true);
                    }}
                    className="w-full"
                  >
                    <X className="h-4 w-4 mr-2" />
                    {t("pages.patrolLogs.clearDates")}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("pages.patrolLogs.dateTime")}</TableHead>
                <TableHead>{t("pages.patrolLogs.guard")}</TableHead>
                <TableHead>{t("pages.patrolLogs.checkpoint")}</TableHead>
                <TableHead>{t("pages.patrolLogs.route")}</TableHead>
                <TableHead>{t("pages.patrolLogs.type")}</TableHead>
                <TableHead>{t("pages.patrolLogs.coordinates")}</TableHead>
                <TableHead className="text-center">{t("pages.patrolLogs.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                  </TableCell>
                </TableRow>
              ) : paginatedLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    {t("pages.patrolLogs.noRecords")}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedLogs.map((log) => {
                  const hasDiscrepancy = hasLocationDiscrepancy(log) && !log.is_reviewed;
                  const distance = getDistanceFromCheckpoint(log);
                  
                  return (
                    <TableRow 
                      key={log.id}
                      className={hasDiscrepancy ? "bg-yellow-50 hover:bg-yellow-100" : ""}
                    >
                    <TableCell className="font-mono text-sm">
                      {formatDateTime(log.timestamp)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-slate-400" />
                        {getUserName(log.user_id)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-slate-400" />
                        {getCheckpointName(log.checkpoint_id)}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {getRouteName(log.route_id)}
                    </TableCell>
                    <TableCell>
                      {log.is_manual ? (
                        <div>
                          <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            {t("pages.patrolLogs.manual")}
                          </Badge>
                          <p className="text-xs text-slate-500 mt-1">
                            {getManualReasonLabel(log.manual_reason)}
                          </p>
                          {log.manual_notes && (
                            <p className="text-xs text-slate-400 mt-0.5 italic">
                              "{log.manual_notes}"
                            </p>
                          )}
                            {log.photo_base64 && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="mt-2 text-xs h-7"
                                onClick={() => {
                                  setSelectedPhoto(log.photo_base64);
                                  setIsPhotoDialogOpen(true);
                                }}
                              >
                                <Image className="h-3 w-3 mr-1" />
                                {t("pages.patrolLogs.viewPhoto")}
                              </Button>
                            )}
                        </div>
                        ) : log.qr_code ? (
                          <Badge className="bg-blue-100 text-blue-700">
                            QR
                          </Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-700">
                          {t("pages.patrolLogs.automatic")}
                        </Badge>
                      )}
                        {hasDiscrepancy && (
                          <div className="mt-2 p-2 bg-yellow-100 border border-yellow-300 rounded text-xs">
                            <div className="flex items-center gap-1 text-yellow-800 font-medium mb-1">
                              <AlertTriangle className="h-3 w-3" />
                              {t("pages.patrolLogs.locationWarning")}
                            </div>
                            <p className="text-yellow-700">
                              {t("pages.patrolLogs.distanceFromCheckpointLabel", { distance: distance ? distance.toFixed(1) : "N/A" })}
                            </p>
                            <p className="text-yellow-600 text-xs mt-1">
                              {(log.is_manual || log.qr_code) 
                                ? t("pages.patrolLogs.manualOutOfRange")
                                : t("pages.patrolLogs.outOfRange")}
                            </p>
                          </div>
                        )}
                        {log.is_reviewed && (
                          <div className="mt-2 flex items-center gap-1 text-xs text-green-600">
                            <CheckCircle2 className="h-3 w-3" />
                            {t("pages.patrolLogs.reviewed")}
                          </div>
                        )}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-slate-500">
                      {log.latitude.toFixed(6)}, {log.longitude.toFixed(6)}
                        {distance !== null && (
                          <div className="text-xs text-slate-400 mt-1">
                            {t("pages.patrolLogs.distanceFromCheckpoint", { distance: distance.toFixed(1) })}
                          </div>
                        )}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-center gap-2">
                        {/* Botón para ver instrucciones si el checkpoint las tiene */}
                        {(() => {
                          const checkpoint = getCheckpoint(log.checkpoint_id);
                          if (checkpoint?.instructions && checkpoint.instructions.length > 0) {
                            return (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedLogForInstructions(log);
                                  setIsInstructionsDialogOpen(true);
                                }}
                                title={t("pages.patrolLogs.viewInstructions")}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <ListChecks className="h-4 w-4" />
                              </Button>
                            );
                          }
                          return null;
                        })()}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openMapDialog(log)}
                          title={t("pages.patrolLogs.viewLocation")}
                        >
                          <Map className="h-4 w-4" />
                        </Button>
                          {hasDiscrepancy && !log.is_reviewed && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleMarkAsReviewed(log)}
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              title={t("pages.patrolLogs.markAsReviewed")}
                            >
                              <CheckCircle2 className="h-4 w-4" />
                            </Button>
                          )}
                        {isAdmin() && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteClick(log)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Eliminar registro"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
        {totalPages > 1 && (
          <div className="border-t p-4">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  // Mostrar primera página, última página, página actual y páginas adyacentes
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <PaginationItem key={page}>
                        <PaginationLink
                          onClick={() => setCurrentPage(page)}
                          isActive={currentPage === page}
                          className="cursor-pointer"
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  } else if (page === currentPage - 2 || page === currentPage + 2) {
                    return (
                      <PaginationItem key={page}>
                        <PaginationEllipsis />
                      </PaginationItem>
                    );
                  }
                  return null;
                })}
                
                <PaginationItem>
                  <PaginationNext
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
            <div className="text-center text-sm text-slate-500 mt-2">
              Mostrando {startIndex + 1} - {Math.min(endIndex, filteredLogs.length)} de {filteredLogs.length} registros
            </div>
          </div>
        )}
      </Card>

      {/* Map Dialog */}
      <Dialog open={isMapDialogOpen} onOpenChange={setIsMapDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              {t("pages.patrolLogs.recordLocation")}
            </DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-600">{t("pages.patrolLogs.guard")}</p>
                  <p className="font-medium">{getUserName(selectedLog.user_id)}</p>
                </div>
                <div>
                  <p className="text-slate-600">{t("pages.patrolLogs.checkpoint")}</p>
                  <p className="font-medium">{getCheckpointName(selectedLog.checkpoint_id)}</p>
                </div>
                <div>
                  <p className="text-slate-600">{t("pages.patrolLogs.dateTime")}</p>
                  <p className="font-medium">{formatDateTime(selectedLog.timestamp)}</p>
                </div>
                <div>
                  <p className="text-slate-600">{t("pages.patrolLogs.coordinates")}</p>
                  <p className="font-mono text-xs">
                    {selectedLog.latitude.toFixed(6)}, {selectedLog.longitude.toFixed(6)}
                  </p>
                </div>
              </div>
              
              <div className="rounded-lg overflow-hidden border border-slate-200" style={{ height: "500px" }}>
                <MapContainer
                  center={[selectedLog.latitude, selectedLog.longitude]}
                  zoom={18}
                  style={{ height: "100%", width: "100%" }}
                  scrollWheelZoom={true}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                    url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                  />
                  
                  {/* Checkpoint radius if available */}
                  {(() => {
                    if (!Array.isArray(checkpoints)) return null;
                    const checkpoint = checkpoints.find(cp => cp.id === selectedLog.checkpoint_id);
                    if (checkpoint) {
                      return (
                        <Circle
                          center={[checkpoint.latitude, checkpoint.longitude]}
                          radius={feetToMeters(checkpoint.radius_feet || 50)}
                          pathOptions={{
                            color: "#0F172A",
                            fillColor: "#0F172A",
                            fillOpacity: 0.1,
                            weight: 2,
                          }}
                        />
                      );
                    }
                    return null;
                  })()}
                  
                  {/* Checkpoint marker if available */}
                  {(() => {
                    if (!Array.isArray(checkpoints)) return null;
                    const checkpoint = checkpoints.find(cp => cp.id === selectedLog.checkpoint_id);
                    if (checkpoint) {
                      return (
                        <Marker position={[checkpoint.latitude, checkpoint.longitude]}>
                          <Popup>
                            <div className="p-2">
                              <h3 className="font-bold text-slate-900">{checkpoint.name}</h3>
                              <p className="text-xs font-mono text-slate-500">
                                {checkpoint.latitude.toFixed(6)}, {checkpoint.longitude.toFixed(6)}
                              </p>
                            </div>
                          </Popup>
                        </Marker>
                      );
                    }
                    return null;
                  })()}
                  
                  {/* Log location marker */}
                  <Marker
                    position={[selectedLog.latitude, selectedLog.longitude]}
                    icon={logIcon}
                  >
                    <Popup>
                      <div className="p-2">
                        <h3 className="font-bold text-blue-600">{t("pages.patrolLogs.recordLocation")}</h3>
                        <p className="text-xs font-mono text-slate-500">
                          {selectedLog.latitude.toFixed(6)}, {selectedLog.longitude.toFixed(6)}
                        </p>
                        <p className="text-xs text-slate-600 mt-1">
                          {formatDateTime(selectedLog.timestamp)}
                        </p>
                      </div>
                    </Popup>
                  </Marker>
                </MapContainer>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Photo Dialog */}
      <Dialog open={isPhotoDialogOpen} onOpenChange={setIsPhotoDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image className="h-5 w-5" />
              {t("pages.patrolLogs.manualRecordPhoto")}
            </DialogTitle>
          </DialogHeader>
          {selectedPhoto && (
            <div className="space-y-4">
              <div className="flex justify-center">
                <img
                  src={selectedPhoto}
                  alt="Foto del registro"
                  className="max-w-full max-h-[600px] rounded-lg border border-slate-200"
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Instructions Dialog */}
      <Dialog open={isInstructionsDialogOpen} onOpenChange={setIsInstructionsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListChecks className="h-5 w-5" />
              {t("pages.patrolLogs.checkpointInstructions")}
            </DialogTitle>
          </DialogHeader>
          {selectedLogForInstructions && (() => {
            const checkpoint = getCheckpoint(selectedLogForInstructions.checkpoint_id);
            // Asegurar que instruction_evidences sea un array
            const evidences = Array.isArray(selectedLogForInstructions.instruction_evidences) 
              ? selectedLogForInstructions.instruction_evidences 
              : [];
            const evidencesMap = {};
            evidences.forEach(ev => {
              if (ev && ev.instruction_id) {
                evidencesMap[ev.instruction_id] = ev;
              }
            });
            
            // Debug: mostrar en consola para verificar
            console.log("Log completo:", selectedLogForInstructions);
            console.log("instruction_evidences del log:", selectedLogForInstructions.instruction_evidences);
            console.log("Evidences procesadas:", evidences);
            console.log("EvidencesMap:", evidencesMap);
            console.log("Checkpoint:", checkpoint);
            console.log("Checkpoint instructions:", checkpoint?.instructions);
            
            if (!checkpoint?.instructions || checkpoint.instructions.length === 0) {
              return (
                <div className="text-center py-8 text-slate-500">
                  {t("pages.patrolLogs.noInstructions")}
                </div>
              );
            }
            
            return (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm border-b pb-4">
                  <div>
                    <p className="text-slate-600">Vigilante</p>
                    <p className="font-medium">{getUserName(selectedLogForInstructions.user_id)}</p>
                  </div>
                  <div>
                    <p className="text-slate-600">Punto de Control</p>
                    <p className="font-medium">{checkpoint.name}</p>
                  </div>
                  <div>
                    <p className="text-slate-600">Fecha/Hora</p>
                    <p className="font-medium">{formatDateTime(selectedLogForInstructions.timestamp)}</p>
                  </div>
                  <div>
                    <p className="text-slate-600">Instrucciones</p>
                    <p className="font-medium">
                      {checkpoint.instructions.filter(inst => {
                        // Contar como completada si tiene evidencia (si la requiere) o si no requiere evidencia
                        if (inst.requires_evidence) {
                          return !!evidencesMap[inst.id];
                        }
                        return true; // Si no requiere evidencia, asumimos completada
                      }).length} / {checkpoint.instructions.length} {t("pages.patrolLogs.completed")}
                    </p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  {checkpoint.instructions
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                    .map((instruction) => {
                      const hasEvidence = evidencesMap[instruction.id];
                      // Una instrucción está completada si:
                      // 1. Requiere evidencia Y tiene evidencia, O
                      // 2. No requiere evidencia (asumimos que fue completada al registrar el checkpoint)
                      const isCompleted = instruction.requires_evidence 
                        ? !!hasEvidence 
                        : true; // Si no requiere evidencia, asumimos que fue completada
                      
                      return (
                        <div
                          key={instruction.id}
                          className={`p-4 rounded-lg border-2 ${
                            isCompleted
                              ? "bg-green-50 border-green-200"
                              : "bg-slate-50 border-slate-200"
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="mt-1">
                              {isCompleted ? (
                                <CheckCircle className="h-5 w-5 text-green-600" />
                              ) : (
                                <X className="h-5 w-5 text-slate-400" />
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className={`font-medium ${
                                  isCompleted ? "text-green-900" : "text-slate-600"
                                }`}>
                                  {instruction.text}
                                </p>
                                {instruction.requires_evidence && (
                                  <Badge variant="outline" className="text-xs">
                                    {t("pages.checkpoints.requiresEvidence")}
                                  </Badge>
                                )}
                              </div>
                              
                              {instruction.requires_evidence && (
                                <div className="mt-2">
                                  {hasEvidence ? (
                                    <div className="space-y-2">
                                      <div className="flex items-center gap-2 text-sm text-green-700">
                                        <CheckCircle className="h-4 w-4" />
                                        <span>{t("pages.patrolLogs.evidenceProvided")}</span>
                                      </div>
                                      <div className="bg-white p-3 rounded border border-green-200">
                                        <div className="flex items-center justify-between mb-2">
                                          <div className="flex items-center gap-2">
                                            {hasEvidence.evidence_type === 'photo' && (
                                              <Image className="h-4 w-4 text-blue-600" />
                                            )}
                                            {hasEvidence.evidence_type === 'video' && (
                                              <Video className="h-4 w-4 text-purple-600" />
                                            )}
                                            {hasEvidence.evidence_type === 'file' && (
                                              <FileText className="h-4 w-4 text-slate-600" />
                                            )}
                                            <span className="text-sm font-medium">
                                              {hasEvidence.file_name || `Evidencia.${hasEvidence.evidence_type === 'photo' ? 'jpg' : hasEvidence.evidence_type === 'video' ? 'mp4' : 'pdf'}`}
                                            </span>
                                          </div>
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                              // Mostrar evidencia según el tipo
                                              if (hasEvidence.evidence_type === 'photo') {
                                                setSelectedPhoto(`data:image/jpeg;base64,${hasEvidence.evidence_data}`);
                                                setIsPhotoDialogOpen(true);
                                              } else if (hasEvidence.evidence_type === 'video') {
                                                // Crear URL temporal para video
                                                const videoUrl = `data:video/mp4;base64,${hasEvidence.evidence_data}`;
                                                const link = document.createElement('a');
                                                link.href = videoUrl;
                                                link.download = hasEvidence.file_name || 'evidencia.mp4';
                                                link.click();
                                              } else {
                                                // Descargar archivo
                                                const link = document.createElement('a');
                                                link.href = `data:application/octet-stream;base64,${hasEvidence.evidence_data}`;
                                                link.download = hasEvidence.file_name || 'evidencia.pdf';
                                                link.click();
                                              }
                                            }}
                                            className="h-7 text-xs"
                                          >
                                            {hasEvidence.evidence_type === 'photo' ? (
                                              <>
                                                <Image className="h-3 w-3 mr-1" />
                                                {t("pages.patrolLogs.view")}
                                              </>
                                            ) : (
                                              <>
                                                <Download className="h-3 w-3 mr-1" />
                                                {t("common.download")}
                                              </>
                                            )}
                                          </Button>
                                        </div>
                                        {hasEvidence.evidence_type === 'photo' && (
                                          <div className="mt-2">
                                            <img
                                              src={`data:image/jpeg;base64,${hasEvidence.evidence_data}`}
                                              alt="Evidencia"
                                              className="max-w-full h-auto rounded border border-slate-200 max-h-48 object-contain"
                                            />
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-2 text-sm text-orange-600">
                                      <AlertTriangle className="h-4 w-4" />
                                      <span>{t("pages.patrolLogs.evidenceNotProvided")}</span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("pages.patrolLogs.deleteRecordConfirm")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("pages.patrolLogs.deleteRecordDescription")}{" "}
              {logToDelete && (
                <>
                  <strong>{getUserName(logToDelete.user_id)}</strong> del{" "}
                  <strong>{formatDateTime(logToDelete.timestamp)}</strong>.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setLogToDelete(null)}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("pages.patrolLogs.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
